import { useState, useRef } from 'react';
import { CalendarCustomization } from '../types';
import { CalendarCustomizerProps } from '../types';
import { CalendarLocationSection } from './calendar/CalendarLocationSection';
import { CalendarImage } from '../types';

const fontOptions = [
  { id: 'inter', name: 'Inter', family: 'Inter, sans-serif' },
  { id: 'playfair', name: 'Playfair Display', family: "'Playfair Display', serif" },
  { id: 'montserrat', name: 'Montserrat', family: 'Montserrat, sans-serif' },
  { id: 'lora', name: 'Lora', family: 'Lora, serif' },
  { id: 'opensans', name: 'Open Sans', family: "'Open Sans', sans-serif" },
  { id: 'roboto', name: 'Roboto', family: 'Roboto, sans-serif' },
  { id: 'merriweather', name: 'Merriweather', family: 'Merriweather, serif' },
  { id: 'raleway', name: 'Raleway', family: 'Raleway, sans-serif' }
];

const defaultLocationState = {
  street: '',
  city: '',
  state: '',
  zip: '',
  agency_combine: '',
  additional_agents: '',
  phone: '',
  cell_phone: '',
  other_info: '',
  calendarImage: import.meta.env.VITE_CALENDAR_DEFAULT_IMAGE || 'calendar-1',
  fontFamily: import.meta.env.VITE_CALENDAR_DEFAULT_FONT || 'Inter, sans-serif',
  textColor: import.meta.env.VITE_CALENDAR_DEFAULT_COLOR || '#000000',
  formScale: parseFloat(import.meta.env.VITE_CALENDAR_DEFAULT_SCALE) || 0.5,
  formOpacity: parseFloat(import.meta.env.VITE_CALENDAR_DEFAULT_OPACITY) || 0.2
};

export function CalendarCustomizer({ onCustomizationChange, initialCustomization, variation, productImages }: CalendarCustomizerProps) {
  const [showSecondary, setShowSecondary] = useState(!!initialCustomization?.locations.secondary);
  const [showTertiary, setShowTertiary] = useState(!!initialCustomization?.locations.tertiary);

  const calendarImages: CalendarImage[] = (() => {
    if (variation?.images && Array.isArray(variation.images) && variation.images.length > 0) {
      const firstImage = variation.images[0];
      if (typeof firstImage === 'object' && 'id' in firstImage && 'url' in firstImage) {
        return (variation.images as CalendarImage[]).slice(0, 3);
      }
    }

    if (productImages && productImages.length > 0) {
      return productImages.slice(0, 3).map((url, index) => ({
        id: `product-image-${index + 1}`,
        url,
        name: `Image ${index + 1}`
      }));
    }

    return [];
  })();

  const [primaryLocation, setPrimaryLocation] = useState(
    initialCustomization?.locations.primary || defaultLocationState
  );

  const [primaryFormPosition, setPrimaryFormPosition] = useState(
    initialCustomization?.locations.primary.formPosition || {
      x: parseFloat(import.meta.env.VITE_CALENDAR_DEFAULT_POSITION_X) || 0,
      y: parseFloat(import.meta.env.VITE_CALENDAR_DEFAULT_POSITION_Y) || 0
    }
  );
  const [isDraggingPrimary, setIsDraggingPrimary] = useState(false);
  const primaryDragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);

  const [secondaryLocation, setSecondaryLocation] = useState(
    initialCustomization?.locations.secondary || defaultLocationState
  );

  const [secondaryFormPosition, setSecondaryFormPosition] = useState(
    initialCustomization?.locations.secondary?.formPosition || {
      x: parseFloat(import.meta.env.VITE_CALENDAR_DEFAULT_POSITION_X) || 0,
      y: parseFloat(import.meta.env.VITE_CALENDAR_DEFAULT_POSITION_Y) || 0
    }
  );
  const [isDraggingSecondary, setIsDraggingSecondary] = useState(false);
  const secondaryDragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);

  const [tertiaryLocation, setTertiaryLocation] = useState(
    initialCustomization?.locations.tertiary || defaultLocationState
  );

  const [tertiaryFormPosition, setTertiaryFormPosition] = useState(
    initialCustomization?.locations.tertiary?.formPosition || {
      x: parseFloat(import.meta.env.VITE_CALENDAR_DEFAULT_POSITION_X) || 0,
      y: parseFloat(import.meta.env.VITE_CALENDAR_DEFAULT_POSITION_Y) || 0
    }
  );
  const [isDraggingTertiary, setIsDraggingTertiary] = useState(false);
  const tertiaryDragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);

  const updateCustomization = (
    primary: typeof primaryLocation,
    secondary?: typeof secondaryLocation,
    tertiary?: typeof tertiaryLocation
  ) => {
    const customization: CalendarCustomization = {
      locations: {
        primary: { ...primary, formPosition: primaryFormPosition },
        ...(secondary && showSecondary ? { secondary: { ...secondary, formPosition: secondaryFormPosition } } : {}),
        ...(tertiary && showTertiary ? { tertiary: { ...tertiary, formPosition: tertiaryFormPosition } } : {})
      }
    };
    onCustomizationChange(customization);
  };

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePrimaryChange = (field: keyof typeof primaryLocation, value: string) => {
    let processedValue: string | number = value;
    if (field === 'phone' || field === 'cell_phone') {
      processedValue = formatPhoneNumber(value);
    } else if (field === 'formScale' || field === 'formOpacity') {
      processedValue = parseFloat(value);
    }
    const updated = { ...primaryLocation, [field]: processedValue };
    setPrimaryLocation(updated);
    updateCustomization(updated, secondaryLocation, tertiaryLocation);
  };

  const handleSecondaryChange = (field: keyof typeof secondaryLocation, value: string) => {
    let processedValue: string | number = value;
    if (field === 'phone' || field === 'cell_phone') {
      processedValue = formatPhoneNumber(value);
    } else if (field === 'formScale' || field === 'formOpacity') {
      processedValue = parseFloat(value);
    }
    const updated = { ...secondaryLocation, [field]: processedValue };
    setSecondaryLocation(updated);
    updateCustomization(primaryLocation, updated, tertiaryLocation);
  };

  const handleTertiaryChange = (field: keyof typeof tertiaryLocation, value: string) => {
    let processedValue: string | number = value;
    if (field === 'phone' || field === 'cell_phone') {
      processedValue = formatPhoneNumber(value);
    } else if (field === 'formScale' || field === 'formOpacity') {
      processedValue = parseFloat(value);
    }
    const updated = { ...tertiaryLocation, [field]: processedValue };
    setTertiaryLocation(updated);
    updateCustomization(primaryLocation, secondaryLocation, updated);
  };

  const handlePrimaryMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).tagName === 'INPUT') return;
    setIsDraggingPrimary(true);
    primaryDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: primaryFormPosition.x,
      initialY: primaryFormPosition.y
    };
  };

  const handleSecondaryMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).tagName === 'INPUT') return;
    setIsDraggingSecondary(true);
    secondaryDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: secondaryFormPosition.x,
      initialY: secondaryFormPosition.y
    };
  };

  const handleTertiaryMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).tagName === 'INPUT') return;
    setIsDraggingTertiary(true);
    tertiaryDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: tertiaryFormPosition.x,
      initialY: tertiaryFormPosition.y
    };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, location: 'primary' | 'secondary' | 'tertiary', scaleFactor: number = 1) => {
    if (location === 'primary' && isDraggingPrimary && primaryDragRef.current) {
      const deltaX = (e.clientX - primaryDragRef.current.startX) / scaleFactor;
      const deltaY = (e.clientY - primaryDragRef.current.startY) / scaleFactor;
      setPrimaryFormPosition({
        x: primaryDragRef.current.initialX + deltaX,
        y: primaryDragRef.current.initialY + deltaY
      });
    } else if (location === 'secondary' && isDraggingSecondary && secondaryDragRef.current) {
      const deltaX = (e.clientX - secondaryDragRef.current.startX) / scaleFactor;
      const deltaY = (e.clientY - secondaryDragRef.current.startY) / scaleFactor;
      setSecondaryFormPosition({
        x: secondaryDragRef.current.initialX + deltaX,
        y: secondaryDragRef.current.initialY + deltaY
      });
    } else if (location === 'tertiary' && isDraggingTertiary && tertiaryDragRef.current) {
      const deltaX = (e.clientX - tertiaryDragRef.current.startX) / scaleFactor;
      const deltaY = (e.clientY - tertiaryDragRef.current.startY) / scaleFactor;
      setTertiaryFormPosition({
        x: tertiaryDragRef.current.initialX + deltaX,
        y: tertiaryDragRef.current.initialY + deltaY
      });
    }
  };

  const handleMouseUp = () => {
    const wasDragging = isDraggingPrimary || isDraggingSecondary || isDraggingTertiary;
    setIsDraggingPrimary(false);
    setIsDraggingSecondary(false);
    setIsDraggingTertiary(false);
    if (wasDragging) {
      updateCustomization(primaryLocation, secondaryLocation, tertiaryLocation);
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <CalendarLocationSection
          title="Primary Location Calendar"
          calendarImages={calendarImages}
          fontOptions={fontOptions}
          location={primaryLocation}
          formPosition={primaryFormPosition}
          altText="Primary Calendar"
          onFieldChange={handlePrimaryChange}
          onImageSelect={(imageId) => handlePrimaryChange('calendarImage', imageId)}
          onFontSelect={(fontFamily) => handlePrimaryChange('fontFamily', fontFamily)}
          onColorSelect={(color) => handlePrimaryChange('textColor', color)}
          onScaleChange={(scale) => handlePrimaryChange('formScale', scale.toString())}
          onMouseDown={handlePrimaryMouseDown}
          onMouseMove={(e, scaleFactor) => handleMouseMove(e, 'primary', scaleFactor)}
          onMouseUp={handleMouseUp}
        />

        {showSecondary && (
          <CalendarLocationSection
            title="Second Location Calendar"
            calendarImages={calendarImages}
            fontOptions={fontOptions}
            location={secondaryLocation}
            formPosition={secondaryFormPosition}
            altText="Secondary Calendar"
            showRemove
            onFieldChange={handleSecondaryChange}
            onImageSelect={(imageId) => handleSecondaryChange('calendarImage', imageId)}
            onFontSelect={(fontFamily) => handleSecondaryChange('fontFamily', fontFamily)}
            onColorSelect={(color) => handleSecondaryChange('textColor', color)}
            onScaleChange={(scale) => handleSecondaryChange('formScale', scale.toString())}
            onRemove={() => setShowSecondary(false)}
            onMouseDown={handleSecondaryMouseDown}
            onMouseMove={(e, scaleFactor) => handleMouseMove(e, 'secondary', scaleFactor)}
            onMouseUp={handleMouseUp}
          />
        )}

        {showTertiary && (
          <CalendarLocationSection
            title="Third Location Calendar"
            calendarImages={calendarImages}
            fontOptions={fontOptions}
            location={tertiaryLocation}
            formPosition={tertiaryFormPosition}
            altText="Tertiary Calendar"
            showRemove
            onFieldChange={handleTertiaryChange}
            onImageSelect={(imageId) => handleTertiaryChange('calendarImage', imageId)}
            onFontSelect={(fontFamily) => handleTertiaryChange('fontFamily', fontFamily)}
            onColorSelect={(color) => handleTertiaryChange('textColor', color)}
            onScaleChange={(scale) => handleTertiaryChange('formScale', scale.toString())}
            onRemove={() => setShowTertiary(false)}
            onMouseDown={handleTertiaryMouseDown}
            onMouseMove={(e, scaleFactor) => handleMouseMove(e, 'tertiary', scaleFactor)}
            onMouseUp={handleMouseUp}
          />
        )}
      </div>

      {!showSecondary && (
        <button
          onClick={() => setShowSecondary(true)}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          + Add Second Location Calendar
        </button>
      )}

      {showSecondary && !showTertiary && (
        <button
          onClick={() => setShowTertiary(true)}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          + Add Third Location Calendar
        </button>
      )}
    </div>
  );
}
